import { createApiClient, unwrap } from '@/services/api/client';
import type { components } from '@/services/api/v1.gen';
import {
  addBreadcrumb,
  getSentryTraceHeaders,
  withSpan,
} from '@/telemetry/sentry';
import { getEntrRequestHeaders } from './requestHeaders';

export type KioskConfig = components['schemas']['KioskConfigResponse'];
export type Partner = components['schemas']['PartnerResponse'];

// 7 retries backing off up to 10 s span ~35 s of 502/503/504, enough to ride
// out the ~30 s window of a backend restart. A timeout is not retried: a hung
// backend would otherwise hold boot for retries × timeout.
const BOOT_FETCH_OPTIONS = {
  timeoutMs: 120_000,
  maxRetries: 7,
  retryOnTimeout: false,
  maxBackoffMs: 10_000,
} as const;

// Same-origin base: the backend is proxied under `/api` on the kiosk's own
// host, and a cross-origin base would add a CORS preflight to every call.
const api = createApiClient({ headers: () => getEntrRequestHeaders() });

export function fetchKioskConfig(slug: string): Promise<KioskConfig> {
  addBreadcrumb('api', 'Fetching kiosk configuration', { slug });
  return withSpan('api.get_kiosk', 'http.client', { 'kiosk.slug': slug }, () =>
    unwrap(
      api.GET('/v1/kiosks/{kiosk_slug}', {
        params: { path: { kiosk_slug: slug } },
        headers: getSentryTraceHeaders(),
        ...api.transport(BOOT_FETCH_OPTIONS),
      })
    )
  );
}

export async function fetchPartner(partnerId: string): Promise<Partner> {
  addBreadcrumb('api', 'Fetching partner data', { partner_id: partnerId });
  const partner = await withSpan(
    'api.get_partner',
    'http.client',
    { 'partner.id': partnerId },
    () =>
      unwrap(
        api.GET('/v1/partners/{partner_id}', {
          params: { path: { partner_id: partnerId } },
          headers: getSentryTraceHeaders(),
          ...api.transport(BOOT_FETCH_OPTIONS),
        })
      )
  );
  addBreadcrumb('api', 'Partner data fetched', {
    partner_id: partnerId,
    partner_title: partner.title,
  });
  return partner;
}
